import { useState } from 'react';
import type { Instance } from '../types';
import { suspendInstance } from '../api';
import './InstanceList.css';

interface InstanceListProps {
  instances: Instance[];
  currentInstance: Instance | null;
  isAtLimit: boolean;
  maxInstances: number;
  chatUrl: string | null;
  onSelect: (instanceId: string) => void;
  onCreateNew: () => void;
  onDelete: (instanceId: string) => void;
  onStart: () => void;
  onChatNavigation: (instanceId: string) => void;
  onRefresh: () => void;
  onStartFastPoll: (instanceId: string, targetStatus: 'running' | 'suspended') => void;
  isQuotaExhausted?: boolean;
}

// Friendly status icons - cute expressions
function getStatusEmoji(status: string): string {
  switch (status) {
    case 'running':
      return '😊';
    case 'creating':
      return '🔧';  // Building/creating
    case 'starting':
      return '🥱';
    case 'suspended':
      return '😴';
    case 'deleting':
      return '👋';  // Waving goodbye
    case 'stopped':
    default:
      return '😶';
  }
}

// User-friendly status text
function getStatusText(status: string): string {
  switch (status) {
    case 'running':
      return 'Active';
    case 'creating':
      return 'Setting up...';  // First-time creation (80-120s)
    case 'starting':
      return 'Waking up...';   // Resume from suspended (1-10s)
    case 'suspended':
      return 'Napping';
    case 'deleting':
      return 'Leaving...';
    case 'stopped':
    default:
      return 'Offline';
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'running':
      return 'var(--status-active)';
    case 'creating':
    case 'starting':
      return 'var(--status-waking)';
    case 'suspended':
      return 'var(--status-napping)';
    case 'deleting':
      return 'var(--status-offline)';
    case 'stopped':
    default:
      return 'var(--status-offline)';
  }
}

// Small inline spinner for buttons
function ButtonSpinner() {
  return <span className="btn-spinner" />;
}

export default function InstanceList({
  instances,
  currentInstance,
  isAtLimit,
  chatUrl,
  onSelect,
  onCreateNew,
  onStart,
  onChatNavigation,
  onRefresh,
  onStartFastPoll,
  isQuotaExhausted = false,
}: InstanceListProps) {
  const [startingInstance, setStartingInstance] = useState(false);
  const [snappingInstance, setSnappingInstance] = useState(false);

  const handleCardClick = (instanceId: string) => {
    onSelect(instanceId);
  };

  const handleStartClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setStartingInstance(true);
    try {
      await onStart();
    } finally {
      setStartingInstance(false);
    }
  };

  const handleSnapClick = async (e: React.MouseEvent, instanceId: string) => {
    e.stopPropagation();
    setSnappingInstance(true);
    try {
      await suspendInstance(instanceId);
      // Refresh instances to update status
      onRefresh();
      // Trigger fast polling for this instance
      onStartFastPoll(instanceId, 'suspended');
    } catch (err) {
      console.error('Failed to suspend instance:', err);
    } finally {
      setSnappingInstance(false);
    }
  };

  const handleChatClick = (e: React.MouseEvent, instanceId: string) => {
    e.stopPropagation();
    onChatNavigation(instanceId);
  };

  const canChat = (status: string) => status === 'running';
  const canStart = (status: string) => status === 'stopped' || status === 'suspended';
  const canSnap = (status: string) => status === 'running';
  const isInProgress = (status: string) => status === 'starting' || status === 'creating' || status === 'deleting';

  return (
    <div className="instance-list">
      <div className="instance-list-header">
        <h2>My Agents</h2>
      </div>

      <div className="instance-grid">
        {instances.map((inst) => {
          const status = inst.status;
          const isSelected = inst.id === currentInstance?.id;

          return (
            <div
              key={inst.id}
              className={`instance-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleCardClick(inst.id)}
            >
              {/* Snap button - only show on running instances */}
              {isSelected && canSnap(status) && (
                <button
                  className="snap-btn"
                  onClick={(e) => handleSnapClick(e, inst.id)}
                  disabled={snappingInstance}
                  title="Put to sleep"
                >
                  {snappingInstance ? <ButtonSpinner /> : '💤'}
                </button>
              )}

              <div className="instance-avatar">
                <span className="avatar-emoji">{getStatusEmoji(status)}</span>
              </div>

              <div className="instance-name">{inst.name}</div>

              <div
                className="instance-status"
                style={{ color: getStatusColor(status) }}
              >
                {getStatusText(status)}
              </div>

              <div className="instance-actions">
                {isSelected && canChat(status) && chatUrl && (
                  <a
                    href={chatUrl}
                    onClick={(e) => handleChatClick(e, inst.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-chat"
                  >
                    💬 Chat
                  </a>
                )}
                {isSelected && canStart(status) && (
                  <button
                    className="btn btn-start"
                    onClick={handleStartClick}
                    disabled={startingInstance || isQuotaExhausted}
                    title={isQuotaExhausted ? 'Quota exhausted - upgrade your plan to continue' : 'Wake up instance'}
                  >
                    {startingInstance && <ButtonSpinner />}
                    {isQuotaExhausted ? '🔒 Quota Used' : '☀️ Wake Up'}
                  </button>
                )}
                {isSelected && isInProgress(status) && (
                  <span className="waking-text">
                    <ButtonSpinner />
                    {getStatusText(status)}
                  </span>
                )}
                {!isSelected && (
                  <span className="select-hint">Tap to select</span>
                )}
              </div>
            </div>
          );
        })}

        {/* Create New Card */}
        {!isAtLimit && (
          <div
            className="instance-card create-card"
            onClick={onCreateNew}
          >
            <div className="instance-avatar create-avatar">
              <span className="avatar-emoji">✨</span>
            </div>
            <div className="instance-name">New Agent</div>
            <div className="instance-status" style={{ color: 'var(--text-muted)' }}>
              Create one
            </div>
          </div>
        )}
      </div>

      {instances.length === 0 && (
        <div className="instance-list-empty">
          <p>No agents yet. Create your first one!</p>
        </div>
      )}
    </div>
  );
}
